import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TelegramIcon from '@mui/icons-material/Telegram';
import TwitterIcon from '@mui/icons-material/Twitter';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import Loader from '../components/Loader';
import { ApiResponse, FundData, TeamMember } from '../types/cryptoRank';

export default function Cryptorank() {
  const [data, setData] = useState<FundData[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [expandedInvestments, setExpandedInvestments] = useState<{ [key: string]: boolean }>({});
  const [expandedTeamMembers, setExpandedTeamMembers] = useState<{ [key: string]: boolean }>({});
  const [isScrapingInvestor, setIsScrapingInvestor] = useState(false);
  const [isScrapingInvestment, setIsScrapingInvestment] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info'>('info');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: '', // 'investor' or 'investment'
  });

  // Auto-dismiss alert after 5 seconds
  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage(null);
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [alertMessage]);

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/crypto-rank/?page=${page}&limit=10`
      );
      const responseData: ApiResponse = response.data;

      if (responseData.success && responseData.data) {
        setData(responseData.data);
        setTotal(responseData.total);
        setCurrentPage(responseData.page);
        setLimit(responseData.limit);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setAlertMessage('Failed to fetch data. Please try again.');
      setAlertSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    fetchData(value);
    // Reset expanded states when changing pages
    setExpandedInvestments({});
    setExpandedTeamMembers({});
  };

  const handleInvestmentsExpand = (id: string) => {
    setExpandedInvestments((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleTeamMembersExpand = (id: string) => {
    setExpandedTeamMembers((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleConfirmDialog = (title: string, message: string, type: string) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      type,
    });
  };

  const handleCloseDialog = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  const handleConfirm = async () => {
    const { type } = confirmDialog;
    handleCloseDialog();
    setLoading(true);

    if (type === 'investor') {
      setIsScrapingInvestor(true);
      setAlertMessage(null);

      try {
        await axios.post(`${process.env.REACT_APP_BASE_URL}/crypto-rank/store`);
        setAlertMessage('Investor data scraped successfully!');
        setAlertSeverity('success');
        await fetchData(currentPage);
      } catch (error) {
        console.error('Error scraping investor data:', error);
        setAlertMessage('Failed to scrape investor data. Please try again.');
        setAlertSeverity('error');
      } finally {
        setIsScrapingInvestor(false);
        setLoading(false);
      }
    } else if (type === 'investment') {
      setIsScrapingInvestment(true);
      setAlertMessage(null);

      try {
        await axios.post(`${process.env.REACT_APP_BASE_URL}/crypto-rank/update-investment-data`);
        setAlertMessage('Investment data scraped successfully!');
        setAlertSeverity('success');
        await fetchData(currentPage);
      } catch (error) {
        console.error('Error scraping investment data:', error);
        setAlertMessage('Failed to scrape investment data. Please try again.');
        setAlertSeverity('error');
      } finally {
        setIsScrapingInvestment(false);
        setLoading(false);
      }
    }
  };

  const handleScrapeInvestor = () => {
    handleConfirmDialog(
      'Confirm Investor Scraping',
      'Are you sure you want to start scraping investor data?',
      'investor'
    );
  };

  const handleScrapeInvestment = () => {
    handleConfirmDialog(
      'Confirm Investment Scraping',
      'Are you sure you want to start scraping investment data?',
      'investment'
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to render social links
  const renderSocialLinks = (
    member: TeamMember,
    fundId: string,
    memberIndex: number,
    prefix = ''
  ) => {
    const links: JSX.Element[] = [];

    if (member.socialData) {
      const { twitter, facebook, instagram, linkedIn, telegram } = member.socialData;

      // LinkedIn
      if (linkedIn?.profileUrls?.length > 0) {
        linkedIn.profileUrls.forEach((url, index) => {
          if (url) {
            links.push(
              <IconButton
                key={`${fundId}-${prefix}member-${memberIndex}-linkedin-${index}`}
                size='small'
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <LinkedInIcon fontSize='small' />
              </IconButton>
            );
          }
        });
      }

      // Twitter
      if (twitter?.profileUrls?.length > 0) {
        twitter.profileUrls.forEach((url, index) => {
          if (url) {
            links.push(
              <IconButton
                key={`${fundId}-${prefix}member-${memberIndex}-twitter-${index}`}
                size='small'
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <TwitterIcon fontSize='small' />
              </IconButton>
            );
          }
        });
      }

      // Facebook
      if (facebook?.profileUrls?.length > 0) {
        facebook.profileUrls.forEach((url, index) => {
          if (url) {
            links.push(
              <IconButton
                key={`${fundId}-${prefix}member-${memberIndex}-facebook-${index}`}
                size='small'
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <FacebookIcon fontSize='small' />
              </IconButton>
            );
          }
        });
      }

      // Instagram
      if (instagram?.profileUrls?.length > 0) {
        instagram.profileUrls.forEach((url, index) => {
          if (url) {
            links.push(
              <IconButton
                key={`${fundId}-${prefix}member-${memberIndex}-instagram-${index}`}
                size='small'
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <InstagramIcon fontSize='small' />
              </IconButton>
            );
          }
        });
      }

      // Telegram
      if (telegram?.profileUrls?.length > 0) {
        telegram.profileUrls.forEach((url, index) => {
          if (url) {
            links.push(
              <IconButton
                key={`${fundId}-${prefix}member-${memberIndex}-telegram-${index}`}
                size='small'
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <TelegramIcon fontSize='small' />
              </IconButton>
            );
          }
        });
      }
    }

    // Fallback to old structure if new structure is not available
    if (links.length === 0 && member.links) {
      member.links.forEach((link, linkIndex) => {
        links.push(
          <IconButton
            key={`${fundId}-${prefix}member-${memberIndex}-link-${linkIndex}`}
            size='small'
            href={link.value}
            target='_blank'
            rel='noopener noreferrer'
          >
            {link.type === 'web' && <LanguageIcon fontSize='small' />}
            {link.type === 'linkedin' && <LinkedInIcon fontSize='small' />}
            {link.type === 'twitter' && <TwitterIcon fontSize='small' />}
          </IconButton>
        );
      });
    }

    return links;
  };

  return (
    <Container sx={{ maxWidth: '1600px !important', p: { xs: 2, md: 3 } }}>
      {/* Action Buttons */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 4,
          background: 'linear-gradient(90deg, #23234a 0%, #2b2c3e 100%)',
          boxShadow: '0 4px 24px 0 rgba(104,113,209,0.10)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
          <Button
            variant='contained'
            onClick={handleScrapeInvestor}
            disabled={isScrapingInvestor}
            startIcon={
              isScrapingInvestor ? <CircularProgress size={20} sx={{ color: '#6871d1' }} /> : null
            }
            sx={{
              background: 'linear-gradient(90deg, #6871d1 0%, #4e54c8 100%)',
              color: '#fff',
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'capitalize',
              height: '44px',
              boxShadow: '0 2px 8px rgba(104,113,209,0.10)',
              px: 3,
              fontSize: '1rem',
              '&:hover': {
                background: '#2b2c3e',
                color: '#6871d1',
              },
            }}
          >
            {isScrapingInvestor ? 'Scraping...' : 'Scrape Investor'}
          </Button>
          <Button
            variant='contained'
            onClick={handleScrapeInvestment}
            disabled={isScrapingInvestment}
            startIcon={
              isScrapingInvestment ? <CircularProgress size={20} sx={{ color: '#6871d1' }} /> : null
            }
            sx={{
              background: 'linear-gradient(90deg, #6871d1 0%, #4e54c8 100%)',
              color: '#fff',
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'capitalize',
              height: '44px',
              boxShadow: '0 2px 8px rgba(104,113,209,0.10)',
              px: 3,
              fontSize: '1rem',
              '&:hover': {
                background: '#2b2c3e',
                color: '#6871d1',
              },
            }}
          >
            {isScrapingInvestment ? 'Scraping...' : 'Scrape Investment'}
          </Button>
        </Stack>

        {/* Pagination Info */}
        {total > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Total: ${total} investors`}
              variant='outlined'
              sx={{
                color: '#6871d1',
                fontWeight: 700,
                background: '#23234a',
                borderColor: '#6871d1',
                px: 2,
                fontSize: '1rem',
              }}
            />
            <Chip
              label={`Page ${currentPage} of ${Math.ceil(total / limit)}`}
              variant='outlined'
              sx={{
                color: '#fff',
                fontWeight: 700,
                background: '#23234a',
                borderColor: '#6871d1',
                px: 2,
                fontSize: '1rem',
              }}
            />
            <Chip
              label={`Showing ${data.length} items`}
              variant='outlined'
              sx={{
                color: '#fff',
                fontWeight: 700,
                background: '#23234a',
                borderColor: '#6871d1',
                px: 2,
                fontSize: '1rem',
              }}
            />
          </Box>
        )}

        {/* Alert Message */}
        {alertMessage && (
          <Alert
            severity={alertSeverity}
            onClose={() => setAlertMessage(null)}
            sx={{
              mb: 2,
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: 2,
              background: alertSeverity === 'success' ? 'rgba(104,113,209,0.08)' : undefined,
            }}
          >
            {alertMessage}
          </Alert>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: '0 4px 24px 0 rgba(104,113,209,0.10)',
          background: '#23234a',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#2b2c3e' }}>
              <TableCell sx={{ color: '#6871d1', fontWeight: 700, fontSize: '1.1rem' }}>
                Logo & Name
              </TableCell>
              <TableCell sx={{ color: '#6871d1', fontWeight: 700, fontSize: '1.1rem' }}>
                Angel Links
              </TableCell>
              <TableCell sx={{ color: '#6871d1', fontWeight: 700, fontSize: '1.1rem' }}>
                Type
              </TableCell>
              <TableCell sx={{ color: '#6871d1', fontWeight: 700, fontSize: '1.1rem' }}>
                Investments
              </TableCell>
              <TableCell sx={{ color: '#6871d1', fontWeight: 700, fontSize: '1.1rem' }}>
                Team Members
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((fund) => (
              <>
                <TableRow
                  key={fund._id}
                  sx={{ background: '#23234a', '&:hover': { background: '#2b2c3e' } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={fund.logo}
                        alt={fund.name}
                        sx={{
                          width: 40,
                          height: 40,
                          boxShadow: '0 2px 8px rgba(104,113,209,0.10)',
                        }}
                      />
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                        {fund.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {Array.isArray(fund.angelLinks) && fund.angelLinks.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {fund.angelLinks.map((link, index) => {
                          // Handle both string and object formats
                          const linkUrl = typeof link === 'string' ? link : link.value || link.url;
                          const linkText =
                            typeof link === 'string' ? link : link.text || link.value || link.url;

                          return (
                            <Link
                              key={`${fund._id}-angel-link-${index}`}
                              href={linkUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              sx={{
                                fontSize: '0.95rem',
                                color: '#6871d1',
                                fontWeight: 600,
                                '&:hover': { textDecoration: 'underline', color: '#fff' },
                              }}
                            >
                              {linkText}
                            </Link>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        N/A
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{fund.type}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {fund.investments && fund.investments.length > 0 ? (
                      <>
                        {fund.investments.slice(0, 2).map((investment, index) => (
                          <Box key={`${fund._id}-investment-${index}`} sx={{ mb: 1 }}>
                            <Typography
                              variant='subtitle2'
                              sx={{ color: '#6871d1', fontWeight: 700 }}
                            >
                              {index + 1}. Name: {investment.coinName}
                            </Typography>
                            <Typography variant='body2'>Stage: {investment.stage}</Typography>
                            <Typography variant='body2'>
                              Category: {investment.categoryName}
                            </Typography>
                            <Typography variant='body2'>
                              Date: {formatDate(investment.date)}
                            </Typography>
                          </Box>
                        ))}
                        {fund.investments.length > 2 && (
                          <Typography
                            variant='body2'
                            color='primary'
                            onClick={() => handleInvestmentsExpand(fund._id)}
                            sx={{
                              cursor: 'pointer',
                              fontWeight: 700,
                              '&:hover': { color: '#6871d1' },
                            }}
                          >
                            +{fund.investments.length - 2} more
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No investments
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {fund.teamMembers && fund.teamMembers.length > 0 ? (
                      <>
                        {fund.teamMembers.slice(0, 2).map((member, index) => (
                          <Box key={`${fund._id}-member-${index}`} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={member.logo}
                                alt={member.name}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  boxShadow: '0 2px 8px rgba(104,113,209,0.10)',
                                }}
                              />
                              <Typography
                                variant='subtitle2'
                                sx={{ color: '#6871d1', fontWeight: 700 }}
                              >
                                {member.name}
                              </Typography>
                            </Box>
                            <Typography variant='body2' color='text.secondary'>
                              {member.jobTitle || (member.jobs && member.jobs.join(', ')) || 'N/A'}
                            </Typography>
                            <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {member.companyName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {renderSocialLinks(member, fund._id, index)}
                            </Box>
                          </Box>
                        ))}
                        {fund.teamMembers.length > 2 && (
                          <Typography
                            variant='body2'
                            color='primary'
                            onClick={() => handleTeamMembersExpand(fund._id)}
                            sx={{
                              cursor: 'pointer',
                              fontWeight: 700,
                              '&:hover': { color: '#6871d1' },
                            }}
                          >
                            +{fund.teamMembers.length - 2} more
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No team members
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow key={`${fund._id}-expanded`}>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    {expandedInvestments[fund._id] &&
                      fund.investments &&
                      fund.investments.length > 0 && (
                        <Box sx={{ margin: 2 }}>
                          <Typography variant='h6' gutterBottom component='div'>
                            All Investments
                          </Typography>
                          <Table size='small'>
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Stage</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {fund.investments.map((investment, index) => (
                                <TableRow key={`${fund._id}-all-investment-${index}`}>
                                  <TableCell>{investment.coinName}</TableCell>
                                  <TableCell>{investment.stage}</TableCell>
                                  <TableCell>{investment.categoryName}</TableCell>
                                  <TableCell>{formatDate(investment.date)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    {expandedTeamMembers[fund._id] &&
                      fund.teamMembers &&
                      fund.teamMembers.length > 0 && (
                        <Box sx={{ margin: 2 }}>
                          <Typography variant='h6' gutterBottom component='div'>
                            All Team Members
                          </Typography>
                          <Table size='small'>
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Links</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {fund.teamMembers.map((member, index) => (
                                <TableRow key={`${fund._id}-all-member-${index}`}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar
                                        src={member.logo}
                                        alt={member.name}
                                        sx={{ width: 24, height: 24 }}
                                      />
                                      {member.name}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{member.companyName}</TableCell>
                                  <TableCell>
                                    {member.jobTitle ||
                                      (member.jobs && member.jobs.join(', ')) ||
                                      'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      {renderSocialLinks(member, fund._id, index, 'all-')}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {total > limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={currentPage}
            onChange={handlePageChange}
            color='primary'
            size='large'
            showFirstButton
            showLastButton
            disabled={loading}
          />
        </Box>
      )}

      {/* Loading Indicator */}
      {loading && <Loader fullScreen />}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color='inherit'>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color='primary' variant='contained' autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
